import { ref, onMounted, computed } from 'vue'
import { listPositions, listWatchlists } from '@/api/client'

export interface StockPresetItem {
  label: string
  value: string
  source?: 'position' | 'watchlist' | 'default'
}

export interface StockPairPresetItem {
  label: string
  x: string
  y: string
  source?: 'position' | 'watchlist' | 'default'
}

const DEFAULT_PRESETS: StockPresetItem[] = [
  { label: '贵州茅台 (600519)', value: 'sh.600519', source: 'default' },
  { label: '五粮液 (000858)', value: 'sz.000858', source: 'default' },
  { label: '中国平安 (601318)', value: 'sh.601318', source: 'default' },
  { label: '招商银行 (600036)', value: 'sh.600036', source: 'default' },
  { label: '宁德时代 (300750)', value: 'sz.300750', source: 'default' },
  { label: '比亚迪 (002594)', value: 'sz.002594', source: 'default' }
]

const DEFAULT_PAIRS: StockPairPresetItem[] = [
  { label: '贵州茅台 (600519) vs 五粮液 (000858)', x: 'sh.600519', y: 'sz.000858', source: 'default' },
  { label: '招商银行 (600036) vs 兴业银行 (601166)', x: 'sh.600036', y: 'sh.601166', source: 'default' },
  { label: '中国平安 (601318) vs 中国太保 (601601)', x: 'sh.601318', y: 'sh.601601', source: 'default' },
  { label: '比亚迪 (002594) vs 宁德时代 (300750)', x: 'sz.002594', y: 'sz.300750', source: 'default' }
]

// 全局响应式缓存，在页面跳转时避免重复拉取
let cachedUserSymbols: string[] | null = null
let cachedPresetItems: StockPresetItem[] | null = null
let cachedPairItems: StockPairPresetItem[] | null = null

/** 清空预设缓存 (登出/换号时调用, 防止上一账号的持仓/自选泄漏给下一账号) */
export function clearUserPresetCache() {
  cachedPresetItems = null
  cachedPairItems = null
  cachedUserSymbols = null
}

// 持仓/自选预设累积器: 去重后的用户标的与预设条目
interface PresetAccumulator {
  items: StockPresetItem[]
  seen: Set<string>
  userSymList: string[]
}

/** 拉取持仓股并入累积器 (失败降级: 仅告警不中断) */
async function appendPositionPresets(acc: PresetAccumulator): Promise<void> {
  try {
    const posRes = await listPositions()
    const posItems = posRes?.items || []
    for (const p of posItems) {
      const sym = p.symbol
      if (sym && !acc.seen.has(sym)) {
        acc.seen.add(sym)
        acc.userSymList.push(sym)
        const name = p.stock_name && p.stock_name !== sym ? p.stock_name : sym
        acc.items.push({
          label: `💼 ${name} (${sym})`,
          value: sym,
          source: 'position',
        })
      }
    }
  } catch (e) {
    console.warn('Failed to load user positions for presets:', e)
  }
}

export const KNOWN_STOCK_NAMES: Record<string, string> = {
  'sh.600028': '中国石化',
  'sz.002415': '海康威视',
  'sh.603596': '伯特利',
  'sh.601012': '隆基绿能',
  'sz.000999': '华润三九',
  'sh.600900': '长江电力',
  'sh.601006': '大秦铁路',
  'sh.600366': '宁波韵升',
  'sh.600172': '黄河旋风',
  'sh.600331': '华胜天成',
  'sz.002236': '大华股份',
  'sh.600938': '中国海油',
  'sh.601857': '中国石油',
  'sh.688836': '铂科新材',
  'sh.688825': '容百科技',
  'sz.002645': '华宏科技',
  'sz.300224': '正海磁材',
  'sh.688981': '中芯国际',
  'sz.000539': '粤电力A',
  'sh.800356': '退市博元',
  'sz.300308': '中际旭创',
  'sz.002085': '万丰奥威',
  'sh.600118': '中国卫星',
  'sh.600616': '金枫酒业',
  'sh.601088': '中国神华',
  'sh.600905': '三峡能源',
  'sh.600642': '申能股份',
  'sz.000333': '美的集团',
  'sz.002028': '思源电气',
  'sz.300274': '阳光电源',
  'sh.600436': '片仔癀',
  'sz.300750': '宁德时代',
  'sh.600111': '北方稀土',
  'sz.002594': '比亚迪',
  'sh.600435': '北方导航',
  'sz.300033': '同花顺',
  'sh.601398': '工商银行',
  'sz.000002': '万科A',
  'sh.600519': '贵州茅台',
  'sz.000858': '五粮液',
  'sh.601318': '中国平安',
  'sz.000001': '平安银行',
  'sh.600276': '恒瑞医药',
  'sh.601888': '中国中免',
  'sz.002475': '立讯精密',
  'sh.600030': '中信证券',
  'sz.000725': '京东方A',
  'sh.601166': '兴业银行',
  'sz.000651': '格力电器',
  'sh.600570': '恒生电子',
  'sh.600571': '信雅达',
  'sz.002468': '申通快递',
  'sh.600036': '招商银行',
  'hk.00700': '腾讯控股',
  'hk.09988': '阿里巴巴',
  'us.AAPL': '苹果公司',
  'us.TSLA': '特斯拉',
  'us.NVDA': '英伟达'
}

/** 从 '💼 招商银行(sh.600036)' 形态标签提取名称; 无 '(' 前缀时返回空串 */
function extractNameFromLabel(label: string, sym: string): string {
  // 锚定前缀剥离 + indexOf 取 '(' 前名称: 等价旧正则且无回溯 (S8786)
  const cleaned = label.replace(/^[💼⭐]\s*/u, '')
  const parenIdx = cleaned.indexOf('(')
  if (parenIdx <= 0) return ''
  const name = cleaned.slice(0, parenIdx).trim()
  return name && name !== sym ? name : ''
}

export function resolvePresetStockName(sym: string): string {
  if (!sym) return ''
  if (KNOWN_STOCK_NAMES[sym]) return KNOWN_STOCK_NAMES[sym]
  if (!cachedPresetItems) return ''
  for (const item of cachedPresetItems) {
    if (item.value === sym && item.label) {
      const name = extractNameFromLabel(item.label, sym)
      if (name) return name
    }
  }
  return ''
}

/** 拉取自选股并入累积器 (失败降级: 仅告警不中断) */
async function appendWatchlistPresets(acc: PresetAccumulator): Promise<void> {
  try {
    const wlList = await listWatchlists()
    for (const wl of wlList) {
      const syms = wl.symbols || []
      for (const sym of syms) {
        if (sym && !acc.seen.has(sym)) {
          acc.seen.add(sym)
          acc.userSymList.push(sym)
          const name = KNOWN_STOCK_NAMES[sym]
          acc.items.push({
            label: name ? `⭐ ${name} (${sym})` : `⭐ ${sym}`,
            value: sym,
            source: 'watchlist',
          })
        }
      }
    }
  } catch (e) {
    console.warn('Failed to load user watchlists for presets:', e)
  }
}

/** 用户标的不足时补充默认样品, 总数封顶 8 */
function fillWithDefaults(items: StockPresetItem[], seen: Set<string>, defaultsToUse: StockPresetItem[]): void {
  for (const d of defaultsToUse) {
    if (!seen.has(d.value) && items.length < 8) {
      items.push(d)
    }
  }
}

/** 由用户标的顺序生成相邻配对, 再补上去重后的默认配对 */
function buildPairPresets(userSymList: string[]): StockPairPresetItem[] {
  const pairs: StockPairPresetItem[] = []
  for (let i = 0; i < Math.min(userSymList.length - 1, 4); i++) {
    const x = userSymList[i]
    const y = userSymList[i + 1]
    pairs.push({
      label: `💼/⭐ ${x} vs ${y}`,
      x,
      y,
      source: 'position',
    })
  }
  for (const p of DEFAULT_PAIRS) {
    if (!pairs.some((ep) => ep.x === p.x && ep.y === p.y)) {
      pairs.push(p)
    }
  }
  return pairs
}

export function useUserStockPresets(customDefaults?: StockPresetItem[]) {
  const presets = ref<StockPresetItem[]>(cachedPresetItems || customDefaults || DEFAULT_PRESETS)
  const pairPresets = ref<StockPairPresetItem[]>(cachedPairItems || DEFAULT_PAIRS)
  const userSymbols = ref<string[]>(cachedUserSymbols || [])
  const loading = ref(false)

  // 快捷填入字符串 (例如 "sh.600519, sz.000858, sh.601318")
  const defaultSymbolsString = computed(() => {
    if (userSymbols.value.length > 0) {
      return userSymbols.value.slice(0, 6).join(', ')
    }
    return 'sh.600519, sz.000858, sh.601318, sz.002594'
  })

  // 首选单股代码
  const firstSymbol = computed(() => {
    if (userSymbols.value.length > 0) {
      return userSymbols.value[0]
    }
    return 'sh.600519'
  })

  async function loadUserPresets() {
    if (cachedPresetItems && cachedPresetItems.length > 0) {
      presets.value = cachedPresetItems
      pairPresets.value = cachedPairItems || DEFAULT_PAIRS
      userSymbols.value = cachedUserSymbols || []
      return
    }

    loading.value = true
    try {
      const acc: PresetAccumulator = { items: [], seen: new Set<string>(), userSymList: [] }

      // 1. 优先提取持仓股 (Positions)
      await appendPositionPresets(acc)

      // 2. 补齐自选股 (Watchlists)
      await appendWatchlistPresets(acc)

      userSymbols.value = acc.userSymList

      const defaultsToUse = customDefaults || DEFAULT_PRESETS
      if (acc.items.length > 0) {
        // 如果用户持仓/自选股少于 6 个，补充默认样品标的
        fillWithDefaults(acc.items, acc.seen, defaultsToUse)
        presets.value = acc.items

        // 为配对交易自动生成持仓/自选配对
        if (acc.userSymList.length >= 2) {
          pairPresets.value = buildPairPresets(acc.userSymList)
        }
      } else {
        presets.value = defaultsToUse
        pairPresets.value = DEFAULT_PAIRS
      }

      cachedUserSymbols = userSymbols.value
      cachedPresetItems = presets.value
      cachedPairItems = pairPresets.value
    } finally {
      loading.value = false
    }
  }

  onMounted(() => {
    loadUserPresets()
  })

  return {
    presets,
    pairPresets,
    userSymbols,
    defaultSymbolsString,
    firstSymbol,
    loading,
    reloadPresets: () => {
      cachedPresetItems = null
      cachedPairItems = null
      cachedUserSymbols = null
      return loadUserPresets()
    },
  }
}
